import { getQuickSightUrl } from "../lib";
import { getDefaultLandingPath } from "../lib/appPermissions";
import { oktaAuth } from "./oktaConfig";
import { setAccessToken } from "../lib/authToken";
import { getApiBearerToken } from "./oktaTokens";

function getUsernameFromClaims(claims: Record<string, unknown>): string {
  return (
    (claims.preferred_username as string) ||
    (claims.email as string) ||
    (claims.sub as string) ||
    ""
  );
}

function hasStoredUserScopes(): boolean {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as { scopes?: unknown };
    return Array.isArray(parsed?.scopes);
  } catch {
    return false;
  }
}

async function fetchAndStoreUserProfile(
  baseUrl: string,
  apiToken: string,
  username: string
): Promise<void> {
  const server = { baseUrl };

  try {
    const response = await getQuickSightUrl({
      url: `${baseUrl}/usermanagement/user/${encodeURIComponent(username)}`,
      server,
      accessToken: apiToken,
      suppressAuthRedirect: true,
    });
    if (response) {
      localStorage.setItem("user", JSON.stringify(response));
    }
  } catch {
    localStorage.setItem(
      "user",
      JSON.stringify({ username, groups: [], scopes: ["demographics"] })
    );
  }
}

/** Load Okta group scopes when missing (e.g. deep-link login without visiting `/`). */
export async function ensureUserProfileLoaded(baseUrl?: string): Promise<void> {
  if (hasStoredUserScopes()) {
    return;
  }

  const resolvedBaseUrl = baseUrl || import.meta.env.VITE_BASE_URL;
  if (!resolvedBaseUrl) {
    return;
  }

  const tokens = await oktaAuth.tokenManager.getTokens();
  const apiToken = getApiBearerToken(tokens);
  const claims = (tokens.idToken?.claims || {}) as Record<string, unknown>;
  const username = getUsernameFromClaims(claims);

  if (!apiToken || !username) {
    return;
  }

  await fetchAndStoreUserProfile(resolvedBaseUrl, apiToken, username);
}

export async function setupUserSession(baseUrl: string): Promise<string | null> {
  const tokens = await oktaAuth.tokenManager.getTokens();
  const apiToken = getApiBearerToken(tokens);
  const claims = (tokens.idToken?.claims || {}) as Record<string, unknown>;
  const username = getUsernameFromClaims(claims);

  if (!apiToken || !username) {
    throw new Error("Unable to resolve user from Okta session.");
  }

  setAccessToken(apiToken);
  localStorage.setItem("accessToken", apiToken);
  if (tokens.idToken?.idToken) {
    localStorage.setItem("idToken", tokens.idToken.idToken);
  }
  if (tokens.accessToken?.accessToken) {
    localStorage.setItem("oktaAccessToken", tokens.accessToken.accessToken);
  }

  localStorage.setItem(
    "currentUser",
    JSON.stringify({
      username,
      name: claims.name,
      email: claims.email,
    })
  );

  const server = { baseUrl };

  await fetchAndStoreUserProfile(baseUrl, apiToken, username);

  try {
    const providerData = await getQuickSightUrl({
      url: `${baseUrl}/referral/provider-id?username=${encodeURIComponent(username)}`,
      server,
      accessToken: apiToken,
      suppressAuthRedirect: true,
    });
    if (providerData) {
      localStorage.setItem("serviceProviderId", providerData?.serviceProviderId || "");
    }
  } catch {
    // Provider ID is optional for some users.
  }

  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") as {
      scopes?: string[];
    };
    return getDefaultLandingPath(user.scopes || []);
  } catch {
    return getDefaultLandingPath();
  }
}
